import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, Tooltip, useMediaQuery } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { Table, Select, message } from 'antd';

import { responsiveSpacing } from '../../themes/responsive';
import { useAuth } from '../../context/AuthContext';
import { fetchClientsList, submitClientSectorProposal } from '../../data/storesApi';

const defaultOptions = {
  sectors: [],
};

const getPendingSector = (record) => record.pendingProposal?.sector;
const getDisplayedSector = (record) => getPendingSector(record) ?? record.sector;

export const SettingsClientsTab = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const [clientsData, setClientsData] = React.useState([]);
  const [options, setOptions] = React.useState(defaultOptions);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [submittingStores, setSubmittingStores] = React.useState({});

  const refreshClients = React.useCallback(async () => {
    if (!currentUser?.customer_id) return;
    const response = await fetchClientsList(currentUser.customer_id);
    setClientsData(response.clients || []);
    setOptions({
      sectors: response.options?.sectors || [],
    });
  }, [currentUser]);

  const loadClients = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await refreshClients();
    } catch (err) {
      console.error('Error loading clients data:', err);
      setError(t('loadClientsError'));
    } finally {
      setLoading(false);
    }
  }, [refreshClients, t]);

  React.useEffect(() => {
    loadClients();
  }, [loadClients]);

  const updateLocalPending = React.useCallback((storeId, value) => {
    setClientsData((prev) => prev.map((client) => {
      if (client.storeId !== storeId) return client;
      return {
        ...client,
        hasPendingProposal: true,
        pendingProposal: {
          id: client.pendingProposal?.id ?? null,
          sector: value,
          proposedBy: client.pendingProposal?.proposedBy || currentUser?.email || null,
          updatedAt: new Date().toISOString(),
        },
      };
    }));
  }, [currentUser]);

  const handleSectorChange = React.useCallback(async (record, value) => {
    if (!currentUser?.customer_id) {
      message.error(t('proposalError'));
      return;
    }

    const currentValue = getDisplayedSector(record);
    if (value === currentValue) {
      return;
    }

    const key = String(record.storeId);
    setSubmittingStores((prev) => ({ ...prev, [key]: true }));
    updateLocalPending(record.storeId, value);

    try {
      await submitClientSectorProposal(currentUser.customer_id, record.storeId, { sector: value });
      message.success(t('proposalSuccess'));
      await refreshClients();
    } catch (submitError) {
      const apiMessage = submitError?.response?.data?.message;
      message.error(apiMessage || t('proposalError'));
      await refreshClients();
    } finally {
      setSubmittingStores((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [currentUser, updateLocalPending, refreshClients, t]);

  const renderSectorCell = React.useCallback((value, record) => {
    const pendingValue = getPendingSector(record);
    const displayedValue = getDisplayedSector(record);
    const key = String(record.storeId);
    const isSubmitting = submittingStores[key];
    const showPending = pendingValue && pendingValue !== record.sector;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Select
          size="small"
          style={{ minWidth: 120 }}
          value={displayedValue ?? undefined}
          onChange={(newValue) => handleSectorChange(record, newValue)}
          options={options.sectors.map((sector) => ({ label: sector, value: sector }))}
          placeholder={t('selectNewSector')}
          disabled={isSubmitting || options.sectors.length === 0}
        />
        {showPending && (
          <Tooltip title={t('proposalPendingTooltip', { value: pendingValue })} placement="top">
            <NotificationsActive fontSize="small" color="warning" />
          </Tooltip>
        )}
      </Box>
    );
  }, [handleSectorChange, options.sectors, submittingStores, t]);

  const columns = React.useMemo(() => ([
    {
      title: t('group'),
      dataIndex: 'group',
      key: 'group',
      sorter: (a, b) => (a.group || '').localeCompare(b.group || ''),
      ellipsis: true,
    },
    {
      title: t('sector'),
      dataIndex: 'sector',
      key: 'sector',
      sorter: (a, b) => (a.sector || '').localeCompare(b.sector || ''),
      render: renderSectorCell,
    },
  ]), [t, renderSectorCell]);

  if (error) {
    return (
      <Box sx={{ ...responsiveSpacing.pagePadding }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('masterClients')}
        </Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...responsiveSpacing.pagePadding }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('masterClients')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('completeClientsList')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table
          columns={columns}
          dataSource={clientsData}
          rowKey={(record) => record.storeId}
          pagination={{
            pageSize: 50,
            simple: isSmallScreen,
            showSizeChanger: !isSmallScreen,
            showQuickJumper: !isSmallScreen,
            showTotal: (total, range) => t('clientsPaginationTotal', {
              start: range[0],
              end: range[1],
              total,
            }),
          }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      )}
    </Box>
  );
};
